import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Sparkles, ArrowRight, User, Phone, Globe, Mail, Calendar, Video,
    Building2, BarChart3, MessageSquare, Bot, Users, Settings, Shield,
    CheckCircle2, Lightbulb, Briefcase, Target, Inbox, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIMEZONES = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'US Eastern (ET)' },
    { value: 'America/Chicago', label: 'US Central (CT)' },
    { value: 'America/Denver', label: 'US Mountain (MT)' },
    { value: 'America/Los_Angeles', label: 'US Pacific (PT)' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
];

const TIPS_BY_ROLE = {
    sales_rep: [
        { icon: Building2, title: 'Companies & Contacts', description: 'Track your accounts, contacts, and their DISC personality profiles to personalize outreach.' },
        { icon: Target, title: 'Pipeline', description: 'Manage deals through stages with drag-and-drop. AI analyzes risk and suggests next steps.' },
        { icon: Inbox, title: 'Inbox', description: 'Send and receive emails directly in the CRM. AI helps you write emails matched to each contact\'s style.' },
        { icon: Bot, title: 'AI Assistant', description: 'Get deal briefings, meeting prep, and communication coaching powered by AI.' },
    ],
    manager: [
        { icon: Building2, title: 'Companies & Contacts', description: 'Track your accounts, contacts, and their DISC personality profiles to personalize outreach.' },
        { icon: Target, title: 'Pipeline', description: 'Manage deals through stages with drag-and-drop. AI analyzes risk and suggests next steps.' },
        { icon: Inbox, title: 'Inbox', description: 'Send and receive emails directly in the CRM. AI helps you write emails matched to each contact\'s style.' },
        { icon: Bot, title: 'AI Assistant', description: 'Get deal briefings, meeting prep, and communication coaching powered by AI.' },
        { icon: Briefcase, title: 'Deal Rooms', description: 'Collaborate with buyers in shared deal rooms with documents, timelines, and mutual action plans.' },
        { icon: BarChart3, title: 'Reports', description: 'Monitor team performance, pipeline health, and revenue forecasts with real-time dashboards.' },
    ],
    admin: [
        { icon: Building2, title: 'Companies & Contacts', description: 'Track your accounts, contacts, and their DISC personality profiles to personalize outreach.' },
        { icon: Target, title: 'Pipeline', description: 'Manage deals through stages with drag-and-drop. AI analyzes risk and suggests next steps.' },
        { icon: Inbox, title: 'Inbox', description: 'Send and receive emails directly in the CRM. AI helps you write emails matched to each contact\'s style.' },
        { icon: Bot, title: 'AI Assistant', description: 'Get deal briefings, meeting prep, and communication coaching powered by AI.' },
        { icon: Shield, title: 'Admin Panel', description: 'Manage your team, assign roles, create custom fields, and configure email templates.' },
        { icon: Settings, title: 'Settings & Integrations', description: 'Connect Gmail, Google Calendar, and configure your organization preferences.' },
        { icon: Users, title: 'User Management', description: 'Invite team members, manage groups, and control permissions across your organization.' },
    ],
};

function StepIndicator({ currentStep, totalSteps }) {
    return (
        <div className="flex items-center gap-2 justify-center mb-8">
            {Array.from({ length: totalSteps }, (_, i) => (
                <div
                    key={i}
                    className={`h-2 rounded-full transition-all duration-300 ${
                        i === currentStep
                            ? 'w-8 bg-stone-900'
                            : i < currentStep
                            ? 'w-2 bg-stone-400'
                            : 'w-2 bg-stone-200'
                    }`}
                />
            ))}
        </div>
    );
}

export default function Onboarding() {
    const { user, getAuthHeader, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [animating, setAnimating] = useState(false);
    const [direction, setDirection] = useState('forward');

    // Step 2 state
    const [title, setTitle] = useState('');
    const [phone, setPhone] = useState('');
    const [timezone, setTimezone] = useState('America/New_York');
    const [savingProfile, setSavingProfile] = useState(false);

    // Step 3 state
    const [integrations, setIntegrations] = useState({
        gmail: false,
        calendar: false,
        zoom: false,
    });

    // Step 5 state
    const [completing, setCompleting] = useState(false);

    const totalSteps = 5;

    const goToStep = (newStep) => {
        if (animating) return;
        setDirection(newStep > step ? 'forward' : 'backward');
        setAnimating(true);
        setTimeout(() => {
            setStep(newStep);
            setAnimating(false);
        }, 200);
    };

    const next = () => goToStep(Math.min(step + 1, totalSteps - 1));

    const handleProfileSave = async () => {
        setSavingProfile(true);
        try {
            await axios.put(`${API}/auth/profile-setup`, { title, phone, timezone }, { headers: getAuthHeader() });
            toast.success('Profile updated');
            next();
        } catch (err) {
            toast.error('Failed to save profile');
        } finally {
            setSavingProfile(false);
        }
    };

    const handleConnectGmail = () => {
        window.location.href = `${API}/oauth/gmail/authorize`;
    };

    const handleConnectCalendar = () => {
        window.location.href = `${API}/oauth/calendar/authorize`;
    };

    const handleComplete = async () => {
        setCompleting(true);
        try {
            await axios.post(`${API}/auth/onboarding-complete`, {}, { headers: getAuthHeader() });
            if (refreshUser) await refreshUser();
            navigate('/dashboard');
        } catch (err) {
            toast.error('Failed to complete onboarding');
        } finally {
            setCompleting(false);
        }
    };

    const userRole = user?.role || 'sales_rep';
    const tips = TIPS_BY_ROLE[userRole] || TIPS_BY_ROLE.sales_rep;

    const renderStep = () => {
        switch (step) {
            case 0:
                return (
                    <div className="text-center space-y-6">
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-stone-900 to-stone-700 flex items-center justify-center mx-auto shadow-lg">
                            <Sparkles className="w-10 h-10 text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-stone-900 font-[Manrope]">
                                Welcome to NurtureCRM{user?.name ? `, ${user.name}` : ''}!
                            </h1>
                            <p className="text-stone-500 mt-2 text-lg">
                                Let's get you set up in just a few steps
                            </p>
                        </div>
                        <Button
                            onClick={next}
                            className="bg-stone-900 hover:bg-stone-800 text-white px-8 py-3 text-base"
                        >
                            Get Started <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                );

            case 1:
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="w-12 h-12 rounded-xl bg-stone-100 flex items-center justify-center mx-auto mb-3">
                                <User className="w-6 h-6 text-stone-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-stone-900 font-[Manrope]">Set Up Your Profile</h2>
                            <p className="text-stone-500 mt-1">Tell us a bit about yourself</p>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="job-title">Job Title</Label>
                                <Input
                                    id="job-title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="e.g. Account Executive"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label htmlFor="phone">Phone Number</Label>
                                <div className="relative mt-1">
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                                    <Input
                                        id="phone"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="+1 (555) 000-0000"
                                        className="pl-10"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="timezone">Timezone</Label>
                                <select
                                    id="timezone"
                                    value={timezone}
                                    onChange={(e) => setTimezone(e.target.value)}
                                    className="w-full mt-1 px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900 focus:border-transparent"
                                >
                                    {TIMEZONES.map((tz) => (
                                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <Button
                            onClick={handleProfileSave}
                            disabled={savingProfile}
                            className="w-full bg-stone-900 hover:bg-stone-800 text-white"
                        >
                            {savingProfile ? 'Saving...' : 'Continue'} <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="w-12 h-12 rounded-xl bg-stone-100 flex items-center justify-center mx-auto mb-3">
                                <Globe className="w-6 h-6 text-stone-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-stone-900 font-[Manrope]">Connect Integrations</h2>
                            <p className="text-stone-500 mt-1">Sync your tools to get the most out of NurtureCRM</p>
                        </div>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between p-4 bg-white border border-stone-200 rounded-xl">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                                        <Mail className="w-5 h-5 text-red-500" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-stone-900 text-sm">Gmail</p>
                                        <p className="text-xs text-stone-400">Send and sync emails</p>
                                    </div>
                                </div>
                                {integrations.gmail ? (
                                    <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-full">
                                        <CheckCircle2 className="w-3.5 h-3.5" /> Connected
                                    </span>
                                ) : (
                                    <Button size="sm" variant="outline" onClick={handleConnectGmail}>
                                        Connect
                                    </Button>
                                )}
                            </div>

                            <div className="flex items-center justify-between p-4 bg-white border border-stone-200 rounded-xl">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                                        <Calendar className="w-5 h-5 text-blue-500" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-stone-900 text-sm">Google Calendar</p>
                                        <p className="text-xs text-stone-400">Sync meetings and events</p>
                                    </div>
                                </div>
                                {integrations.calendar ? (
                                    <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-full">
                                        <CheckCircle2 className="w-3.5 h-3.5" /> Connected
                                    </span>
                                ) : (
                                    <Button size="sm" variant="outline" onClick={handleConnectCalendar}>
                                        Connect
                                    </Button>
                                )}
                            </div>

                            <div className="flex items-center justify-between p-4 bg-white border border-stone-200 rounded-xl">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                                        <Video className="w-5 h-5 text-indigo-500" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-stone-900 text-sm">Zoom</p>
                                        <p className="text-xs text-stone-400">Schedule video meetings</p>
                                    </div>
                                </div>
                                <Button size="sm" variant="outline" disabled>
                                    Coming Soon
                                </Button>
                            </div>
                        </div>
                        <div className="flex items-center justify-between pt-2">
                            <button
                                onClick={next}
                                className="text-sm text-stone-400 hover:text-stone-600 transition-colors"
                            >
                                Skip for now
                            </button>
                            <Button onClick={next} className="bg-stone-900 hover:bg-stone-800 text-white">
                                Continue <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="w-12 h-12 rounded-xl bg-stone-100 flex items-center justify-center mx-auto mb-3">
                                <Lightbulb className="w-6 h-6 text-stone-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-stone-900 font-[Manrope]">Quick Tips</h2>
                            <p className="text-stone-500 mt-1">Here's what you can do with NurtureCRM</p>
                        </div>
                        <div className="grid gap-3">
                            {tips.map((tip, i) => {
                                const Icon = tip.icon;
                                return (
                                    <div key={i} className="flex items-start gap-3 p-3 bg-white border border-stone-200 rounded-xl">
                                        <div className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <Icon className="w-4.5 h-4.5 text-stone-600" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-stone-900 text-sm">{tip.title}</p>
                                            <p className="text-xs text-stone-500 mt-0.5 leading-relaxed">{tip.description}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <Button onClick={next} className="w-full bg-stone-900 hover:bg-stone-800 text-white">
                            Continue <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                );

            case 4:
                return (
                    <div className="text-center space-y-6">
                        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-stone-900 font-[Manrope]">You're all set!</h2>
                            <p className="text-stone-500 mt-2 text-lg">
                                Your CRM is ready to go. Start building relationships and closing deals.
                            </p>
                        </div>
                        <Button
                            onClick={handleComplete}
                            disabled={completing}
                            className="bg-stone-900 hover:bg-stone-800 text-white px-8 py-3 text-base"
                        >
                            {completing ? 'Setting up...' : 'Go to Dashboard'} <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#FAFAF5' }}>
            <div className="w-full max-w-lg">
                <StepIndicator currentStep={step} totalSteps={totalSteps} />
                <div
                    className={`bg-white rounded-2xl shadow-sm border border-stone-200 p-8 transition-all duration-200 ${
                        animating
                            ? direction === 'forward'
                                ? 'opacity-0 translate-x-4'
                                : 'opacity-0 -translate-x-4'
                            : 'opacity-100 translate-x-0'
                    }`}
                >
                    {renderStep()}
                </div>
                {step > 0 && step < 4 && (
                    <button
                        onClick={() => goToStep(step - 1)}
                        className="mt-4 text-sm text-stone-400 hover:text-stone-600 transition-colors mx-auto block"
                    >
                        Back
                    </button>
                )}
            </div>
        </div>
    );
}
